import React, { useState, useEffect, useRef } from 'react'; // Adicione useRef
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
    Box, Flex, Heading, Button, VStack, FormControl, FormLabel, Input,
    Select as NativeSelect, Grid, HStack, Wrap, Tag, TagLabel,
    TagCloseButton, useToast, Spinner, Icon, Text,
    // Importações para o modal de confirmação
    useDisclosure,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
} from '@chakra-ui/react';
import { Select as ChakraReactSelect } from 'chakra-react-select';
import { FaArrowLeft, FaTrashAlt, FaPaperclip } from 'react-icons/fa';
// Importe a nova função da API
import { getTicket, getTicketMasterData, updateTicket, deleteTicket } from '../services/api';
import { WikiEditor, docHasContent } from '../components/WikiEditor';

const TicketEditPage = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { ticketId } = useParams();
    
    // Hooks para o modal de confirmação
    const { isOpen, onOpen, onClose } = useDisclosure();
    const cancelRef = useRef();

    // --- LÓGICA PARA PEGAR O USUÁRIO DO LOCALSTORAGE ---
    const [currentUser, setCurrentUser] = useState(null);
    useEffect(() => {
        const userString = window.localStorage.getItem("user");
        if (userString) {
            setCurrentUser(JSON.parse(userString));
        } else {
            navigate('/login');  
        }
    }, [navigate]);
    // --- FIM DA LÓGICA ---

    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false); // Estado para o loading da remoção
    const [masterData, setMasterData] = useState({ categories: [], users: [] });
    
    const [formData, setFormData] = useState(null);
    const [existingAttachments, setExistingAttachments] = useState([]);
    const [attachmentsToDelete, setAttachmentsToDelete] = useState([]);
    const [newFiles, setNewFiles] = useState([]);
    const [linkedItems, setLinkedItems] = useState([]);
    const [currentItemId, setCurrentItemId] = useState('');

    useEffect(() => {
        const fetchAllData = async () => {
            setLoading(true);
            try {
                const [ticketRes, masterDataRes] = await Promise.all([
                    getTicket(ticketId),
                    getTicketMasterData()
                ]);

                const ticketToEdit = ticketRes.data.data;

                if (currentUser && ticketToEdit.author.id !== currentUser.id) {
                    toast({
                        title: "Acesso Negado",
                        description: "Você não tem permissão para editar este ticket.",
                        status: "error",
                        isClosable: true,
                    });
                    navigate(`/tickets/${ticketId}`);
                    return;
                }

                setMasterData(masterDataRes.data);
                setFormData({
                    subject: ticketToEdit.subject,
                    description: ticketToEdit.description || '',
                    priority: ticketToEdit.priority,
                    status: ticketToEdit.status,
                    categories: ticketToEdit.category ? [{ value: ticketToEdit.category.id, label: ticketToEdit.category.name }] : [],
                    assignees: ticketToEdit.assignees.map(a => ({ value: a.id, label: a.name })),
                    dueDate: ticketToEdit.due_date ? new Date(ticketToEdit.due_date).toISOString().split('T')[0] : '',
                });
                setLinkedItems(ticketToEdit.linkedItems || []);
                setExistingAttachments(ticketToEdit.attachments || []);

            } catch (error) {
                toast({ title: "Erro ao carregar dados do ticket.", status: 'error', isClosable: true });
                navigate('/tickets');
            } finally {
                setLoading(false);
            }
        };
        
        if (currentUser) {
            fetchAllData();
        }
    }, [ticketId, toast, navigate, currentUser]);

    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    const addLinkedItem = () => { if (currentItemId && !linkedItems.includes(currentItemId)) { setLinkedItems(prev => [...prev, currentItemId]); setCurrentItemId(''); } };
    const removeLinkedItem = (idToRemove) => setLinkedItems(prev => prev.filter(id => id !== idToRemove));
    
    const handleFileChange = (e) => setNewFiles([...e.target.files]);
    const markAttachmentForDeletion = (attachmentId) => {
        setAttachmentsToDelete(prev => [...prev, attachmentId]);
        setExistingAttachments(prev => prev.filter(att => att.id !== attachmentId));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        const submissionData = new FormData();
        submissionData.append('_method', 'PUT');
        Object.entries(formData).forEach(([key, value]) => {
            if (key === 'categories' || key === 'assignees') return;
            submissionData.append(key, value);
        });
        formData.categories.forEach(c => submissionData.append('categoryIds[]', c.value));
        formData.assignees.forEach(a => submissionData.append('assigneeIds[]', a.value));
        linkedItems.forEach(item => submissionData.append('linkedItems[]', item));
        newFiles.forEach(file => submissionData.append('files[]', file));
        attachmentsToDelete.forEach(id => submissionData.append('attachments_to_delete[]', id));
        try {
            await updateTicket(ticketId, submissionData);
            toast({ title: "Ticket atualizado com sucesso!", status: 'success', isClosable: true });
            navigate(`/tickets/${ticketId}`);
        } catch (error) {
            toast({ title: "Erro ao atualizar ticket.", description: error.response?.data?.message, status: 'error', isClosable: true });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Função para lidar com a remoção do ticket
    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            await deleteTicket(ticketId);
            toast({
                title: 'Ticket Removido',
                description: 'O ticket foi removido com sucesso da base de dados.',
                status: 'success',
                duration: 5000,
                isClosable: true,
            });
            navigate('/tickets'); // Redireciona para a lista de tickets
        } catch (error) {
            toast({
                title: 'Erro ao remover o ticket.',
                description: error.response?.data?.message || 'Ocorreu um erro inesperado.',
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
        } finally {
            setIsDeleting(false);
            onClose(); // Fecha o modal de confirmação
        }
    };

    if (loading || !formData) {
        return <Flex justify="center" align="center" h="100vh"><Spinner size="xl" /></Flex>;
    }
    
    const categoryOptions = masterData.categories.map(cat => ({ value: cat.id, label: cat.name }));
    const userOptions = masterData.users.map(user => ({ value: user.id, label: user.name }));

    return (
        <Box p={{ base: 4, md: 8 }} bg="gray.50" minH="100vh">
            <Box maxW="1000px" mx="auto">
                <Flex justify="space-between" align="center" mb={4}>
                    <Button as={RouterLink} to="/tickets" size="sm" leftIcon={<FaArrowLeft />}>Voltar para a Lista</Button>
                    <Button as={RouterLink} to={`/tickets/${ticketId}`} size="sm">Voltar aos Detalhes</Button>
                </Flex>
                <Box bg="white" p={8} borderRadius="md" shadow="sm">
                    <form onSubmit={handleSubmit}>
                        <VStack spacing={6} align="stretch">
                            <Heading as="h1" size="lg">Editar Ticket #{ticketId}</Heading>
                            {/* ... (O resto do seu formulário permanece igual) ... */}
                            <FormControl isRequired><FormLabel>Assunto</FormLabel><Input name="subject" value={formData.subject} onChange={handleChange} /></FormControl> 
                            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr 1fr' }} gap={6}>
                                <FormControl isRequired><FormLabel>Prioridade</FormLabel><NativeSelect name="priority" value={formData.priority} onChange={handleChange}><option value="baixa">Baixa</option><option value="media">Média</option><option value="alta">Alta</option><option value="urgente">Urgente</option></NativeSelect></FormControl>
                                <FormControl isRequired><FormLabel>Status</FormLabel><NativeSelect name="status" value={formData.status} onChange={handleChange}><option value="aberto">Aberto</option><option value="em_andamento">Em Andamento</option><option value="bloqueado">Bloqueado</option><option value="fechado">Concluido</option></NativeSelect></FormControl>
                                <FormControl><FormLabel>Prazo</FormLabel><Input name="dueDate" type="date" value={formData.dueDate} onChange={handleChange} /></FormControl>
                            </Grid>
                            <FormControl isRequired><FormLabel>Categorias</FormLabel><ChakraReactSelect isMulti name="categories" options={categoryOptions} placeholder="Selecione..." value={formData.categories} onChange={(opts) => setFormData(prev => ({ ...prev, categories: opts || [] }))} /></FormControl>
                            <FormControl><FormLabel>Descrição</FormLabel><WikiEditor value={formData.description} onChange={({ json }) => setFormData(prev => ({ ...prev, description: docHasContent(json) ? JSON.stringify(json) : '' }))} placeholder="Descreva o ticket... Digite '/' para blocos, cole Markdown ou imagens." minHeight="260px" /></FormControl>
                            <FormControl><FormLabel>Atribuir para</FormLabel><ChakraReactSelect isMulti name="assignees" options={userOptions} placeholder="Selecione..." value={formData.assignees} onChange={(opts) => setFormData(prev => ({ ...prev, assignees: opts || [] }))} /></FormControl>
                            <FormControl>
                                <FormLabel>Anexos</FormLabel>
                                <VStack align="stretch" p={4} borderWidth={1} borderRadius="md" spacing={4}>
                                    {existingAttachments.length > 0 && (
                                        <VStack align="stretch">
                                            <Text fontWeight="bold" fontSize="sm">Anexos existentes:</Text>
                                            {existingAttachments.map(att => (
                                                <HStack key={att.id} justify="space-between">
                                                    <Text fontSize="sm" color="blue.500">{att.name}</Text>
                                                    <Button size="xs" colorScheme="red" variant="ghost" onClick={() => markAttachmentForDeletion(att.id)}><Icon as={FaTrashAlt} /></Button>
                                                </HStack>
                                            ))}
                                        </VStack>
                                    )}
                                    <Box>
                                        <FormLabel htmlFor="files-upload" _hover={{ cursor: 'pointer', bg: 'gray.100' }} borderWidth={2} borderStyle="dashed" p={4} textAlign="center" borderRadius="md">
                                            <Icon as={FaPaperclip} mr={2} /> Clique para adicionar novos anexos
                                        </FormLabel>
                                        <Input id="files-upload" type="file" multiple onChange={handleFileChange} display="none" />
                                        {newFiles.length > 0 && <Text fontSize="xs" mt={2}>{newFiles.length} arquivo(s) selecionado(s).</Text>}
                                    </Box>
                                </VStack>
                            </FormControl>
                            <FormControl><FormLabel>Itens Vinculados</FormLabel><VStack align="stretch" p={4} borderWidth={1} borderRadius="md"><Wrap>{linkedItems.map(item => (<Tag size="lg" key={item} borderRadius="full" variant="solid" colorScheme="gray"><TagLabel>{item}</TagLabel><TagCloseButton onClick={() => removeLinkedItem(item)} /></Tag>))}</Wrap><HStack><Input placeholder="Digite o ID" value={currentItemId} onChange={(e) => setCurrentItemId(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addLinkedItem(); } }} /><Button onClick={addLinkedItem} colorScheme="blue">Adicionar</Button></HStack></VStack></FormControl>
                            <Flex justify="flex-end" mt={4}>
                                <Button variant="ghost" mr={3} onClick={() => navigate(`/tickets/${ticketId}`)} isDisabled={isSubmitting}>Cancelar</Button>
                                <Button type="submit" colorScheme="blue" isLoading={isSubmitting} loadingText="Salvando...">Salvar Alterações</Button>
                            </Flex>
                        </VStack>
                    </form>
                    
                    <Box mt={10} pt={6} borderTop="2px" borderColor="red.200">
                        <Button
                            colorScheme="red"
                            leftIcon={<FaTrashAlt />}
                            onClick={onOpen}
                            isDisabled={isSubmitting || isDeleting}
                        >
                            Remover este Ticket
                        </Button>
                    </Box>

                    {/* --- MODAL DE CONFIRMAÇÃO --- */}
                    <AlertDialog
                        isOpen={isOpen}
                        leastDestructiveRef={cancelRef}
                        onClose={onClose}
                        isCentered
                    >
                        <AlertDialogOverlay>
                            <AlertDialogContent>
                                <AlertDialogHeader fontSize="lg" fontWeight="bold">
                                    Remover Ticket #{ticketId}
                                </AlertDialogHeader>

                                <AlertDialogBody>
                                    Você tem certeza? Esta ação é **permanente** e não pode ser desfeita. Todos os dados, incluindo comentários e anexos, serão apagados para sempre.
                                </AlertDialogBody>

                                <AlertDialogFooter>
                                    <Button ref={cancelRef} onClick={onClose} isDisabled={isDeleting}>
                                        Cancelar
                                    </Button>
                                    <Button colorScheme="red" onClick={handleDelete} ml={3} isLoading={isDeleting} loadingText="Removendo...">
                                        Confirmar Remoção
                                    </Button>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialogOverlay>
                    </AlertDialog>

                </Box>
            </Box>
        </Box>
    );
};

export default TicketEditPage;